import type { ButtonProps as UtilityButtonProps } from '@components/utility/buttons';
import { Button, CloseButton } from '@components/utility/buttons';
import {
    Box,
    Dialog,
    DialogActions,
    DialogActionsProps,
    DialogContent,
    DialogContentProps,
    DialogProps,
    DialogTitle,
    DialogTitleProps,
    Fade,
    IconButtonProps,
    SxProps,
    Theme,
    Typography,
    TypographyProps,
} from '@mui/material';
import { Key, ReactNode } from 'react';

export type GenericActionDialogButton = {
    key?: Key;
    label: ReactNode;
    buttonProps?: Omit<UtilityButtonProps, 'children'>;
};

export type GenericActionDialogButtons =
    | [GenericActionDialogButton]
    | [GenericActionDialogButton, GenericActionDialogButton];

type Props = Omit<DialogProps, 'children' | 'slotProps' | 'title' | 'content'> & {
    icon?: ReactNode;
    iconColor?: string;
    iconBackgroundColor?: string;
    title: ReactNode;
    content: ReactNode;
    buttons: GenericActionDialogButtons;
    viewKey?: Key;
    onExited?: () => void;
    closeButtonProps?: IconButtonProps;
    titleProps?: DialogTitleProps;
    titleTypographyProps?: TypographyProps;
    contentProps?: DialogContentProps;
    actionsProps?: DialogActionsProps;
    paperSx?: SxProps<Theme>;
};

const GenericActionDialog = ({
    icon,
    iconColor,
    iconBackgroundColor = 'neutral-100.main',
    title,
    content,
    buttons,
    viewKey,
    onExited,
    closeButtonProps,
    titleProps,
    titleTypographyProps,
    contentProps,
    actionsProps,
    paperSx,
    ...dialogProps
}: Props) => {
    const transitionKey = viewKey ?? 'default';
    const paperStyles: SxProps<Theme> = [
        {
            minWidth: 0,
            maxWidth: 450,
        },
        ...(paperSx ? (Array.isArray(paperSx) ? paperSx : [paperSx]) : []),
    ];

    return (
        <Dialog
            maxWidth="xs"
            fullWidth
            {...dialogProps}
            slotProps={{
                transition: { onExited },
                paper: {
                    sx: paperStyles,
                },
            }}
        >
            <CloseButton
                {...closeButtonProps}
                onClick={closeButtonProps?.onClick ?? ((event) => dialogProps.onClose?.(event, 'escapeKeyDown'))}
            />
            <DialogTitle display="flex" alignItems="center" gap="18px" {...titleProps}>
                {icon && (
                    <Box
                        width={50}
                        height={50}
                        borderRadius="5px"
                        bgcolor={iconBackgroundColor}
                        flexShrink={0}
                        className="black-border"
                        alignItems="center"
                        justifyContent="center"
                        display="flex"
                        sx={
                            iconColor
                                ? {
                                      color: iconColor,
                                      '& svg': {
                                          color: iconColor,
                                          fill: iconColor,
                                      },
                                  }
                                : undefined
                        }
                    >
                        {icon}
                    </Box>
                )}
                <Typography fontFamily="museo-sans" fontSize={18} fontWeight={600} {...titleTypographyProps}>
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent {...contentProps}>
                <Fade in key={transitionKey}>
                    <Box>{content}</Box>
                </Fade>
            </DialogContent>
            <DialogActions
                className="flex-col gap-[12px] black-border border-0 border-t p-[16px] pt-[24px] bg-[#F9FAFB]"
                disableSpacing
                {...actionsProps}
            >
                <Fade in key={`${String(transitionKey)}-actions`}>
                    <Box className="w-full flex flex-col gap-[12px]">
                        {buttons.map(({ key, label, buttonProps }, index) => (
                            <Button key={key ?? index} fullRounded fullWidth {...buttonProps}>
                                {label}
                            </Button>
                        ))}
                    </Box>
                </Fade>
            </DialogActions>
        </Dialog>
    );
};

export { GenericActionDialog };
